import axios from "axios";
import { updateProject } from "./projectStore";

const BASE = process.env.REACT_APP_BACKEND_URL;

const d = (offsetDays: number) =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString();

export async function seedCCBNicheProject(token: string): Promise<string> {
  const authHeader = { Authorization: `Bearer ${token}` };

  // ── 1. Create project ─────────────────────────────────────────────────────
  const { data: project } = await axios.post(`${BASE}/api/projects`, {
    name: "Niche Research — AI Tools Affiliate",
    description:
      "Running CCB's niche-opportunity-finder skill: identify and validate an untapped AI/SaaS affiliate niche with ≥3 programs at 20%+ commission.",
    status: "active",
    methodology: "agile",
    sprintLength: 2,
    diagramText: `flowchart LR
  A[Clarify Goals] --> B[Generate 8-12 Candidates]
  B --> C[Score on 4 Dimensions]
  C --> D[Validate Top 3 on CCB]
  D --> E[Write Opportunity Brief]
  E --> F[Map to Next Skill]
  F --> G[affiliate-program-search]
  F --> H[commission-calculator]`,

    stakeholders: [
      {
        id: "sh-1",
        name: "Affiliate Marketer",
        role: "Primary User",
        interest: "Identify a profitable niche to start promoting within 1 hour",
        influence: "high",
        email: "",
      },
      {
        id: "sh-2",
        name: "Content Team",
        role: "Consumer of Output",
        interest: "Receive actionable content angles for the top niche",
        influence: "medium",
        email: "",
      },
      {
        id: "sh-3",
        name: "Affiliate Program Manager",
        role: "External Partner",
        interest: "Quality traffic from well-researched niche content",
        influence: "low",
        email: "",
      },
    ],

    documents: [
      {
        id: "doc-1",
        type: "brd",
        title: "Niche Opportunity Brief — AI Tools",
        content: `# Niche Opportunity Brief

## Top Niche: AI Productivity Tools
**Tier:** A (Trending)
**Score:** 8.4 / 10

| Dimension | Score | Weight | Weighted |
|---|---|---|---|
| Search Demand | 9 | 30% | 2.7 |
| Program Availability | 8 | 30% | 2.4 |
| Competition Level | 7 | 25% | 1.75 |
| Content Potential | 9 | 15% | 1.35 |

## Why This Niche
- AI tools market growing 35% YoY
- Buyers are professionals with high LTV
- SaaS programs pay 20–40% recurring commission
- Low barrier: no need for personal expertise, just honest reviews

## Example Programs
1. **Jasper AI** — 30% recurring, 60-day cookie
2. **Copy.ai** — 45% first month + 20% recurring
3. **Writesonic** — 30% recurring lifetime

## Content Angles
1. "Best AI writing tools for non-native English speakers"
2. "ChatGPT vs Jasper: which saves more time for marketers?"
3. "5 AI tools that replaced my VA (with honest pricing)"

## Runner-Up: AI Image Generation
**Score:** 7.1 — fewer high-commission programs, more competition

## Next Steps
1. Run \`affiliate-program-search\` for Jasper, Copy.ai, Writesonic
2. Run \`commission-calculator\` for 90-day projections
3. Create first article using Blog & SEO skills`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],

    userStories: [
      {
        id: "us-1",
        title: "Generate niche candidates",
        asA: "affiliate marketer",
        iWant: "8-12 niche candidates across Trending, Evergreen, and Micro-niche tiers",
        soThat: "I have enough options to compare objectively",
        acceptanceCriteria: [
          "At least 3 candidates from Tier A (trending)",
          "At least 3 from Tier B (evergreen)",
          "At least 2 Tier C micro-niches",
          "Each candidate has a brief rationale",
        ],
        priority: "must",
        storyPoints: 3,
        status: "done",
        startDate: new Date(Date.now() - 10 * 86400000).toISOString(),
        dueDate: new Date(Date.now() - 8 * 86400000).toISOString(),
        estimatedDays: 2,
      },
      {
        id: "us-2",
        title: "Score niches on 4 dimensions",
        asA: "affiliate marketer",
        iWant: "each niche scored on Search Demand, Program Availability, Competition, Content Potential",
        soThat: "I can compare them with a single objective score",
        acceptanceCriteria: [
          "Weights applied correctly (30/30/25/15)",
          "Each score backed by a data source or SERP check",
          "Final scores shown in sortable table",
        ],
        priority: "must",
        storyPoints: 5,
        status: "done",
        startDate: new Date(Date.now() - 8 * 86400000).toISOString(),
        dueDate: new Date(Date.now() - 5 * 86400000).toISOString(),
        estimatedDays: 3,
      },
      {
        id: "us-3",
        title: "Validate top 3 on CCB",
        asA: "affiliate marketer",
        iWant: "the top 3 niches verified against list.affitor.com",
        soThat: "I know programs are real, active, and paying",
        acceptanceCriteria: [
          "Each niche has ≥3 programs with 20%+ commission confirmed",
          "Cookie window ≥30 days on top niche",
          "No programs flagged as low-quality or inactive",
        ],
        priority: "must",
        storyPoints: 3,
        status: "in-progress",
        startDate: new Date(Date.now() - 4 * 86400000).toISOString(),
        dueDate: new Date(Date.now() + 1 * 86400000).toISOString(),
        estimatedDays: 5,
      },
      {
        id: "us-4",
        title: "Deliver opportunity brief",
        asA: "affiliate marketer",
        iWant: "a structured brief for the winning niche with content angles and next steps",
        soThat: "I can start promoting within 1 hour of receiving it",
        acceptanceCriteria: [
          "Top niche named with final score",
          "3 specific content angles listed",
          "Next skill mapped (affiliate-program-search)",
          "Runner-up summarised in 2 lines",
        ],
        priority: "must",
        storyPoints: 2,
        status: "backlog",
        startDate: new Date(Date.now() + 2 * 86400000).toISOString(),
        dueDate: new Date(Date.now() + 4 * 86400000).toISOString(),
        estimatedDays: 2,
      },
    ],
  }, { headers: authHeader });

  const projectId: string = project.id;

  // ── 2. Seed requirements via tools API ────────────────────────────────────
  const requirements = [
    {
      title: "Generate 8-12 niche candidates",
      raw_text:
        "The system must identify between 8 and 12 niche candidates covering all three tiers: Trending (A), Evergreen (B), and Micro-niche (C).",
      req_type: "functional",
      priority: "must",
      domain: "research",
    },
    {
      title: "Top niche has ≥3 programs at 20%+ commission",
      raw_text:
        "The winning niche must have at least 3 verified affiliate programs each offering minimum 20% commission rate. Verified via list.affitor.com.",
      req_type: "functional",
      priority: "must",
      domain: "validation",
    },
    {
      title: "Search demand backed by data",
      raw_text:
        "Every niche score for Search Demand dimension must cite a specific data source (Google Trends, Ahrefs, SimilarWeb, or SERP inspection).",
      req_type: "non-functional",
      priority: "must",
      domain: "quality",
    },
    {
      title: "Content angles must be specific",
      raw_text:
        "Content angles in the brief must name a specific audience segment and angle — not generic titles like 'AI tool review'.",
      req_type: "non-functional",
      priority: "should",
      domain: "quality",
    },
    {
      title: "Self-validation checklist must pass",
      raw_text:
        "Before delivery, the skill must run its internal validation: all four scoring dimensions complete, programs verified, brief formatted correctly.",
      req_type: "constraint",
      priority: "must",
      domain: "process",
    },
  ];

  for (const req of requirements) {
    try {
      await axios.post(
        `${BASE}/api/tools/requirements`,
        { ...req, project_id: projectId },
        { headers: authHeader }
      );
    } catch {
      // non-fatal — requirements panel will show what was saved
    }
  }

  const tryPost = async (url: string, body: object) => {
    try {
      const { data } = await axios.post(url, body, { headers: authHeader });
      return data;
    } catch {
      return null;
    }
  };

  // ── 3. CAPA entries ───────────────────────────────────────────────────────
  const capas = [
    {
      project_id: projectId,
      title: "Commission tier misread for Copy.ai — 45% quoted as ongoing recurring",
      description: "Draft brief cited Copy.ai at a flat 45% recurring rate. Cross-check against the network dashboard showed 45% is first-month only, dropping to 20% recurring after.",
      root_cause: "affiliate-program-search output pulled the headline commission number without distinguishing first-month bonus from steady-state recurring rate.",
      corrective_action: "Correct the brief to show both figures (45% month 1 / 20% recurring). Re-run commission-calculator with the split rate for accurate 90-day projections.",
      preventive_action: "Add a required 'rate structure' field (flat / tiered / first-month+recurring) to every program entry before it can be cited in a brief.",
      status: "open",
      priority: "high",
      due_date: d(3),
      raised_by: "Affiliate Marketer",
    },
    {
      project_id: projectId,
      title: "Content angle overlap across 3 draft articles",
      description: "Listicle, comparison post, and how-to tutorial all opened with the same angle ('AI tools for non-native English speakers'), risking keyword cannibalization.",
      root_cause: "Content angles were assigned before checking against the keyword-cluster-architect map, so no dedup pass ran.",
      corrective_action: "Reassign 2 of the 3 articles to unused angles from the ranked list. Reserve the shared angle for the highest-authority piece only.",
      preventive_action: "Require angle-uniqueness check against the keyword cluster map as part of Definition of Done for any documents-tab content.",
      status: "open",
      priority: "medium",
      due_date: d(7),
      raised_by: "Content Team",
    },
  ];

  for (const capa of capas) {
    await tryPost(`${BASE}/api/tools/capa`, capa);
  }

  // ── 4. Suppliers (affiliate programs + research sources) ──────────────────
  const suppliers = [
    {
      project_id: projectId,
      name: "Jasper AI",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "partners@jasper.ai",
      notes: "30% recurring, 60-day cookie. Network: PartnerStack. Verified active on list.affitor.com.",
    },
    {
      project_id: projectId,
      name: "Copy.ai",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "affiliates@copy.ai",
      notes: "45% first month, 20% recurring thereafter. Network: FirstPromoter. Rate structure corrected per CAPA-1.",
    },
    {
      project_id: projectId,
      name: "Writesonic",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "affiliates@writesonic.com",
      notes: "30% recurring, lifetime. Network: Rewardful. Added after list.affitor.com re-search (see Evolution Log).",
    },
    {
      project_id: projectId,
      name: "list.affitor.com",
      category: "Research Platform",
      risk_tier: "minor",
      contact_email: "",
      notes: "Primary source for program discovery, commission verification, and cookie-window data. Free to use.",
    },
  ];

  for (const supplier of suppliers) {
    await tryPost(`${BASE}/api/tools/suppliers`, supplier);
  }

  // ── 5. Test Cases ───────────────────────────────────────────────────────────
  const testCases = [
    {
      project_id: projectId,
      title: "SEO audit — 'Jasper AI vs Copy.ai' comparison article",
      description: "Run the 10-dimension SEO scorecard against the published comparison post before it's linked from the opportunity brief.",
      steps: [
        "Open seo-audit skill against the published article URL",
        "Confirm all 10 dimensions scored (title tag, meta desc, H1 structure, internal links, etc.)",
        "Flag any dimension scoring below 6/10",
        "Verify affiliate disclosure appears above the fold",
      ],
      expected_result: "Overall score ≥ 8/10. No dimension below 6. Disclosure present and conspicuous.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "A/B test — headline variants for 'Best AI Writing Tools' listicle",
      description: "Validate ab-test-generator output produces distinct, testable headline variants for the top-performing listicle.",
      steps: [
        "Generate 3 headline variants via ab-test-generator",
        "Confirm each variant targets a different emotional hook (curiosity, urgency, authority)",
        "Deploy variants via bio-link-deployer with even traffic split",
        "Track CTR for 7 days via conversion-tracker",
      ],
      expected_result: "3 distinct variants deployed. CTR data collected for all 3 with statistically valid sample size.",
      priority: "should",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Content decay check — 90-day re-audit of top niche article",
      description: "Confirm content-decay-detector correctly flags ranking drops on the flagship AI-tools article after 90 days live.",
      steps: [
        "Run content-decay-detector against article URL at day 90",
        "Compare current SERP position vs day-30 baseline",
        "If drop > 5 positions, trigger refresh recommendation",
        "Log outcome to Evolution Log",
      ],
      expected_result: "Detector correctly identifies decay/no-decay. Refresh recommendation triggers only when threshold exceeded.",
      priority: "should",
      status: "pending",
    },
  ];

  for (const tc of testCases) {
    await tryPost(`${BASE}/api/tools/test-cases`, tc);
  }

  // ── 6. Evolution Log ──────────────────────────────────────────────────────
  const evolutionEntries = [
    {
      project_id: projectId,
      change_type: "scope_add",
      title: "Add Writesonic as third program candidate",
      description: "Initial brief scoped only Jasper AI and Copy.ai. A follow-up list.affitor.com search surfaced Writesonic with a stronger lifetime-recurring structure.",
      requested_by: "Affiliate Marketer",
      impact: "Adds 1 program to validate and 1 content angle to the brief. No timeline impact.",
      status: "approved",
      created_at: d(-6),
    },
    {
      project_id: projectId,
      change_type: "requirement_change",
      title: "Commission threshold raised from 15% to 20%",
      description: "Original validation requirement set the minimum commission bar at 15%, which passed too many low-value programs through to the brief stage.",
      requested_by: "Affiliate Marketer",
      impact: "Tightens candidate pool, reduces validation workload per niche. Requirement doc updated same day.",
      status: "approved",
      created_at: d(-9),
    },
    {
      project_id: projectId,
      change_type: "scope_remove",
      title: "Drop AI Image Generation niche from active research",
      description: "Runner-up niche scored 7.1 vs 8.4 for AI Productivity Tools — fewer high-commission programs and higher competition. Deprioritized rather than dropped entirely.",
      requested_by: "Affiliate Marketer",
      impact: "Frees up research time for deeper validation of the winning niche. Kept as backlog candidate for next cycle.",
      status: "approved",
      created_at: d(-4),
    },
  ];

  for (const entry of evolutionEntries) {
    await tryPost(`${BASE}/api/tools/evolution-logs`, entry);
  }

  // ── 7. Knowledge Base documents ───────────────────────────────────────────
  const kbDocs = [
    {
      project_id: projectId,
      title: "Affiliate Commission Structures — Reference",
      doc_type: "reference",
      source: "Program terms, verified list.affitor.com",
      content: `# Commission Structure Types

## Flat Recurring
Fixed % of every payment for the customer's lifetime (e.g., Writesonic 30% lifetime).

## First-Month + Recurring
Higher one-time rate on the first payment, lower rate ongoing (e.g., Copy.ai 45%/20%). Always model both figures separately — quoting only the first-month rate overstates 90-day projections.

## Tiered
Rate increases with referral volume (common on larger SaaS programs, not used by current candidates).

## Cookie Window
Days after click during which a conversion still credits the affiliate. 30 days minimum bar for this project; 60 days (Jasper) is strong.`,
    },
    {
      project_id: projectId,
      title: "SEO Scoring Rubric — 10 Dimensions",
      doc_type: "technical",
      source: "seo-audit skill spec",
      content: `# SEO Audit — 10 Dimensions

1. Title tag optimization
2. Meta description CTR potential
3. H1/H2 structure
4. Internal linking depth
5. Keyword density (primary + LSI)
6. Content length vs SERP median
7. Image alt-text coverage
8. Page load speed
9. Mobile responsiveness
10. Affiliate disclosure placement

Score each 0-10. Overall pass bar for this project: ≥8/10 average, no single dimension below 6.`,
    },
    {
      project_id: projectId,
      title: "FTC Disclosure Requirements — Affiliate Content",
      doc_type: "policy",
      source: "FTC Endorsement Guides",
      content: `# FTC Affiliate Disclosure Rules

- Disclosure must be "clear and conspicuous" — not buried in a footer or About page.
- Must appear before the affiliate link, not after.
- Platform-specific: Reddit requires disclosure in the post body, not just a bio link.
- Video/TikTok: disclosure must be in both the caption and spoken/on-screen within the first few seconds.
- Applies to every published piece before it counts as compliant in the Compliance tab.`,
    },
  ];

  for (const doc of kbDocs) {
    await tryPost(`${BASE}/api/tools/documents`, doc);
  }

  // ── 8. Compliance Rules ───────────────────────────────────────────────────
  const rules = [
    {
      project_id: projectId,
      name: "FTC — Affiliate Link Disclosure",
      description: "Every piece of published content containing an affiliate link must carry a clear and conspicuous disclosure appearing before the link, per FTC Endorsement Guides.",
      domain: "marketing",
      market: "US",
      channel: "content",
      contains_phi: false,
    },
    {
      project_id: projectId,
      name: "Platform ToS — Reddit self-promotion limits",
      description: "Reddit affiliate posts must disclose in-body and respect subreddit self-promotion ratio rules (commonly 9:1 non-promotional to promotional). Violation risks account ban.",
      domain: "marketing",
      market: "global",
      channel: "reddit",
      contains_phi: false,
    },
  ];

  for (const rule of rules) {
    await tryPost(`${BASE}/api/tools/rules`, rule);
  }

  return projectId;
}
